import React, { useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { FiMaximize2, FiX } from "react-icons/fi";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";
import LiveCandlestickChart from "./LiveCandlestickChart";
import MultiPaneStockChart from "./MultiPaneStockChart";
import SubchartsStockLines from "./SubchartsStockLines";
import TodaysPLBreakdownChart from "./TodaysPLBreakdownChart";

const parseHexRgb = (hex) => {
  if (!hex || typeof hex !== "string") return { r: 59, g: 130, b: 246 };

  const normalized = hex.replace("#", "");
  const fullHex =
    normalized.length === 3
      ? normalized
        .split("")
        .map((char) => char + char)
        .join("")
      : normalized;

  if (fullHex.length !== 6) return { r: 59, g: 130, b: 246 };

  return {
    r: Number.parseInt(fullHex.slice(0, 2), 16),
    g: Number.parseInt(fullHex.slice(2, 4), 16),
    b: Number.parseInt(fullHex.slice(4, 6), 16),
  };
};

const hexToRgba = (hex, alpha) => {
  const { r, g, b } = parseHexRgb(hex);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const CustomChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;

  const pointValue = Number(payload[0]?.value || 0);

  return (
    <div
      style={{
        minWidth: "170px",
        background: "linear-gradient(180deg, rgba(255,255,255,0.98) 0%, #F8FBFF 100%)",
        border: "1px solid rgba(36, 87, 191, 0.12)",
        borderRadius: "14px",
        boxShadow: "0 18px 40px rgba(15, 23, 42, 0.12)",
        padding: "12px 14px",
        backdropFilter: "blur(8px)",
      }}
    >
      <div
        style={{
          fontSize: "11px",
          fontWeight: 700,
          color: "#2457BF",
          letterSpacing: "0.04em",
          textTransform: "uppercase",
          marginBottom: "8px",
        }}
      >
        {label}
      </div>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "12px",
        }}
      >
        <span
          style={{
            fontSize: "12px",
            color: "#64748B",
            fontWeight: 600,
          }}
        >
          Price
        </span>
        <span
          style={{
            fontSize: "15px",
            color: "#0F172A",
            fontWeight: 700,
          }}
        >
          ₹
          {pointValue.toLocaleString("en-IN", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })}
        </span>
      </div>
    </div>
  );
};

const getIconBackground = (chartColor) => {
  const accentMap = {
    "#3B82F6": "#EBF5FF",
    "#8B5CF6": "#F3E8FF",
    "#EF4444": "#FEE2E2",
    "#10B981": "#DCFCE7",
    "#F59E0B": "#FEF3C7",
  };

  return accentMap[chartColor] || "#EBF5FF";
};

const CHART_BOTTOM_BAR_WIDTH_PX = 5;
const CHART_BOTTOM_BAR_GAP_PX = 3;
const CHART_BOTTOM_LANE_HEIGHT_PX = 46;
const CHART_BOTTOM_MAX_BAR_HEIGHT_PX = 40;
const CHART_BOTTOM_BAR_COUNT = 32;

/** Same decorative silhouette on every card — not tied to `data` or live values. */
const STATIC_CARD_BOTTOM_BAR_HEIGHTS = [
  0.45, 0.68, 0.4, 0.72, 0.52, 0.66, 0.38, 0.7, 0.48, 0.64, 0.42, 0.69, 0.55, 0.44,
  0.62, 0.5, 0.74, 0.46, 0.6, 0.53, 0.67, 0.41, 0.71, 0.57, 0.43, 0.65, 0.49, 0.63,
  0.51, 0.58, 0.47, 0.61,
];

/** Decorative bars in normal flow; tighter gap; fixed bar width. */
const ChartBottomBars = ({ chartColor, heights }) => (
  <div
    aria-hidden
    style={{
      width: "100%",
      padding: "2px 14px 12px",
      boxSizing: "border-box",
      flexShrink: 0,
      pointerEvents: "none",
      display: "flex",
      justifyContent: "center",
      overflow: "hidden",
    }}
  >
    <div
      style={{
        display: "flex",
        alignItems: "flex-end",
        justifyContent: "center",
        gap: `${CHART_BOTTOM_BAR_GAP_PX}px`,
        flexWrap: "nowrap",
        maxWidth: "100%",
        height: `${CHART_BOTTOM_LANE_HEIGHT_PX}px`,
      }}
    >
      {heights.map((h, i) => (
        <div
          key={i}
          style={{
            width: `${CHART_BOTTOM_BAR_WIDTH_PX}px`,
            flexShrink: 0,
            height: `${Math.max(7, Math.round(h * CHART_BOTTOM_MAX_BAR_HEIGHT_PX))}px`,
            borderRadius: "2px 2px 1px 1px",
            background: `linear-gradient(180deg, ${hexToRgba(chartColor, 0.14)} 0%, ${hexToRgba(
              chartColor,
              0.042,
            )} 100%)`,
          }}
        />
      ))}
    </div>
  </div>
);

const ChartCard = ({
  title,
  value,
  chartColor,
  iconSrc,
  modalChartType,
  modalChartBasePrice,
  modalChartSeed,
  modalTrendBias,
  data = [],
  isHovered,
  onMouseEnter,
  onMouseLeave,
  showExpandButton = true,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [activeRange, setActiveRange] = useState("1D");
  const numericValue = Number(String(value).replace(/[^0-9.-]/g, "")) || 100;
  const modalTabTheme = {
    primary: "#2457BF",
    primaryDark: "#1A42A0",
    surface: "#F8FAFC",
    border: "#E2E8F0",
    text: "#475569",
  };

  const defaultData =
    data.length > 0
      ? data
      : [
        { name: "09:15", value: numericValue * 0.985 },
        { name: "10:00", value: numericValue * 0.992 },
        { name: "10:45", value: numericValue * 0.988 },
        { name: "11:30", value: numericValue * 1.003 },
        { name: "12:15", value: numericValue * 0.997 },
        { name: "13:30", value: numericValue * 1.011 },
        { name: "14:30", value: numericValue * 1.006 },
        { name: "15:30", value: numericValue },
      ];

  const timeSeriesMap = useMemo(() => {
    const intradayLabels = [
      "09:15",
      "09:45",
      "10:15",
      "10:45",
      "11:15",
      "11:45",
      "12:15",
      "12:45",
      "13:15",
      "13:45",
      "14:15",
      "14:45",
      "15:15",
      "15:30",
    ];
    const weeklyLabels = ["Mon", "Tue", "Wed", "Thu", "Fri"];
    const monthlyLabels = ["1 Apr", "5 Apr", "9 Apr", "12 Apr", "16 Apr", "20 Apr", "24 Apr", "30 Apr"];

    const buildTradingSeries = (labels, volatility, drift) => {
      let previous = numericValue * (1 - volatility * 1.4);

      return labels.map((label, idx) => {
        const wave = Math.sin((idx / Math.max(labels.length - 1, 1)) * Math.PI * 2.6);
        const pullback = Math.cos((idx / Math.max(labels.length - 1, 1)) * Math.PI * 4) * volatility * 0.35;
        const movement = numericValue * (drift * idx + wave * volatility + pullback);
        const nextValue =
          idx === labels.length - 1
            ? numericValue
            : Math.max(0, previous + movement * 0.55 + numericValue * drift * 0.2);

        previous = nextValue;

        return {
          name: label,
          value: Number(nextValue.toFixed(2)),
        };
      });
    };

    return {
      "1D": buildTradingSeries(intradayLabels, 0.008, 0.0008),
      "1W": buildTradingSeries(weeklyLabels, 0.015, 0.0018),
      "1M": buildTradingSeries(monthlyLabels, 0.026, 0.0026),
    };
  }, [numericValue]);

  const activeChartData = timeSeriesMap[activeRange] || defaultData;
  const latestValue =
    activeChartData[activeChartData.length - 1]?.value ??
    defaultData[defaultData.length - 1]?.value ??
    0;
  const useLiveCandles = modalChartType === "candlestick";
  const useMultiPaneSubcharts = modalChartType === "multi-pane";
  const useSubchartsLines = modalChartType === "subcharts-lines";
  const useTodaysBreakdown = modalChartType === "todays-breakdown";

  const stopPropagate = (event) => {
    event.stopPropagation();
  };

  return (
    <>
      <div
        className="chart-card"
        style={{
          position: "relative",
          backgroundColor: "white",
          cursor: "pointer",
          border: "1px solid #e5e7eb",
          borderRadius: "12px",
          overflow: "hidden",
          transition: "all 0.3s ease",
          boxShadow: isHovered
            ? "0 8px 24px rgba(0,0,0,0.12)"
            : "0 2px 8px rgba(0,0,0,0.06)",
          transform: isHovered ? "translateY(-2px)" : "translateY(0)",
          minHeight: "158px",
          display: "flex",
          flexDirection: "column",
        }}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
      >
        <div
          style={{
            flex: 1,
            padding: "13px 14px 16px",
            display: "flex",
            alignItems: "flex-start",
            gap: "12px",
            position: "relative",
            zIndex: 1,
          }}
        >
          <div
            className="chart-card-icon"
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: "40px",
              height: "40px",
              borderRadius: "8px",
              backgroundColor: getIconBackground(chartColor),
            }}
          >
            {iconSrc ? (
              <img
                src={iconSrc}
                alt={`${title} icon`}
                style={{
                  width: "22px",
                  height: "22px",
                  objectFit: "contain",
                }}
              />
            ) : (
              <i
                className={
                  chartColor === "#3B82F6" || chartColor === "#EF4444"
                    ? "icon-investment-amount"
                    : "icon-current-value"
                }
                style={{
                  fontSize: "18px",
                  color: chartColor,
                }}
              ></i>
            )}
          </div>

          <div
            style={{
              flex: 1,
              minHeight: "40px",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              position: "relative",
              paddingRight: showExpandButton ? "40px" : "0",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "flex-start",
                gap: "10px",
                marginBottom: "10px",
              }}
            >
              <h6
                style={{
                  margin: 0,
                  fontSize: "12px",
                  fontWeight: "600",
                  lineHeight: "1.1",
                  color: "#6B7280",
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                }}
              >
                {title}
              </h6>
              {/* {showExpandButton && (
                <button
                  type="button"
                  aria-label={`Expand ${title} chart`}
                  onClick={(event) => {
                    stopPropagate(event);
                    setIsExpanded(true);
                  }}
                  style={{
                    border: "none",
                    outline: "none",
                    width: "30px",
                    height: "30px",
                    position: "absolute",
                    top: 0,
                    right: 0,
                    borderRadius: "8px",
                    background: "#F3F4F6",
                    color: "#4B5563",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                    transition: "all 0.2s ease",
                  }}
                >
                  <FiMaximize2 size={14} />
                </button>
              )} */}
            </div>

            <div
              style={{
                fontSize: "20px",
                fontWeight: "700",
                color: "#111827",
                margin: 0,
                lineHeight: "1.2",
              }}
            >
              {value}
            </div>
          </div>
        </div>

        <ChartBottomBars chartColor={chartColor} heights={STATIC_CARD_BOTTOM_BAR_HEIGHTS} />
      </div>

      <AnimatePresence>
        {isExpanded ? (
          <motion.div
            onClick={() => setIsExpanded(false)}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.22 }}
            style={{
              position: "fixed",
              inset: 0,
              background: "rgba(17,24,39,0.45)",
              backdropFilter: "blur(2px)",
              zIndex: 2000,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              padding: "16px",
            }}
          >
            <motion.div
              onClick={stopPropagate}
              initial={{ opacity: 0, y: 18, scale: 0.96 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 18, scale: 0.98 }}
              transition={{ type: "spring", damping: 28, stiffness: 280 }}
              style={{
                width: "min(760px, 100%)",
                background: "#FFFFFF",
                borderRadius: "18px",
                boxShadow: "0 24px 60px rgba(15, 23, 42, 0.18)",
                border: "1px solid #E2E8F0",
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  padding: "18px 20px",
                  borderBottom: "1px solid #EEF2F7",
                  background:
                    "linear-gradient(180deg, rgba(248,250,252,0.95) 0%, rgba(255,255,255,1) 100%)",
                }}
              >
                <div>
                  <h5
                    style={{
                      margin: 0,
                      fontSize: "16px",
                      fontWeight: "700",
                      color: "#111827",
                    }}
                  >
                    {title} Trend
                  </h5>
                  <p style={{ margin: "4px 0 0", color: "#6B7280", fontSize: "13px" }}>
                    Current: {value}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setIsExpanded(false)}
                  style={{
                    border: "none",
                    width: "32px",
                    height: "32px",
                    borderRadius: "8px",
                    background: "#F3F4F6",
                    color: "#4B5563",
                    display: "inline-flex",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  <FiX size={15} />
                </button>
              </div>

              <div style={{ padding: "16px 18px 20px" }}>
                <div style={{ display: "flex", gap: "8px", marginBottom: "16px" }}>
                  {["1D", "1W", "1M"].map((range) => (
                    <button
                      key={range}
                      type="button"
                      onClick={() => setActiveRange(range)}
                      style={{
                        border: "none",
                        borderRadius: "999px",
                        padding: "7px 14px",
                        fontSize: "12px",
                        fontWeight: "600",
                        background:
                          activeRange === range
                            ? `linear-gradient(135deg, ${modalTabTheme.primary} 0%, ${modalTabTheme.primaryDark} 100%)`
                            : modalTabTheme.surface,
                        color: activeRange === range ? "#fff" : modalTabTheme.text,
                        boxShadow:
                          activeRange === range
                            ? "0 8px 20px rgba(36, 87, 191, 0.22)"
                            : `inset 0 0 0 1px ${modalTabTheme.border}`,
                        transition: "all 0.2s ease",
                      }}
                    >
                      {range}
                    </button>
                  ))}
                </div>

                <div
                  style={{
                    height: "300px",
                    width: "100%",
                    background: useSubchartsLines ? "#F8FAFC" : "#FCFDFE",
                    border: useSubchartsLines ? "1px solid #D5DDE8" : "1px solid #EEF2F7",
                    borderRadius: "14px",
                    padding: "8px 10px 6px",
                  }}
                >
                  {useTodaysBreakdown ? (
                    <TodaysPLBreakdownChart
                      seed={modalChartSeed || 1}
                      range={activeRange}
                    />
                  ) : useSubchartsLines ? (
                    <SubchartsStockLines
                      basePrice={modalChartBasePrice || numericValue}
                      seed={modalChartSeed || 1}
                      trendBias={modalTrendBias || 1}
                      range={activeRange}
                    />
                  ) : useMultiPaneSubcharts ? (
                    <MultiPaneStockChart
                      basePrice={modalChartBasePrice || numericValue}
                      seed={modalChartSeed || 1}
                      trendBias={modalTrendBias || 1}
                      range={activeRange}
                    />
                  ) : useLiveCandles ? (
                    <LiveCandlestickChart
                      basePrice={modalChartBasePrice || numericValue}
                      seed={modalChartSeed || 1}
                      trendBias={modalTrendBias || 1}
                      range={activeRange}
                    />
                  ) : (
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={activeChartData} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
                        <CartesianGrid stroke="#E5E7EB" strokeDasharray="3 3" vertical={false} />
                        <Line
                          type="monotone"
                          dataKey="value"
                          stroke={chartColor}
                          strokeWidth={2.5}
                          dot={false}
                          activeDot={{ r: 4, stroke: "#fff", strokeWidth: 2 }}
                        />
                        <XAxis
                          dataKey="name"
                          tick={{ fill: "#94A3B8", fontSize: 11 }}
                          axisLine={false}
                          tickLine={false}
                          minTickGap={24}
                        />
                        <YAxis
                          tick={{ fill: "#94A3B8", fontSize: 11 }}
                          axisLine={false}
                          tickLine={false}
                          width={64}
                          domain={["dataMin - 1", "dataMax + 1"]}
                          tickFormatter={(val) => `₹${Number(val).toLocaleString("en-IN", {
                            maximumFractionDigits: 0,
                          })}`}
                        />
                        <Tooltip
                          cursor={{ stroke: "#2457BF", strokeWidth: 1, strokeDasharray: "4 4" }}
                          content={<CustomChartTooltip />}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  )}
                </div>

                <div style={{ marginTop: "12px", color: "#6B7280", fontSize: "12px" }}>
                  Latest value: <strong style={{ color: "#111827" }}>₹{latestValue.toLocaleString("en-IN")}</strong>
                </div>
              </div>
            </motion.div>
          </motion.div>
        ) : null}
      </AnimatePresence>
    </>
  );
};

export default ChartCard;
